/**
 * @file Параметры и типы для Field1
 * @module membrane/src/visual/field1/field1Params
 * 
 * Определяет типы данных и параметры для визуального слоя 1.
 * Очень светлый, почти белый визуал с мягкими облаками.
 */

/**
 * Параметры поля из Mirror API
 */
export interface Field1Params {
  seed: number;
  energy: number;
  turbulence: number;
  cohesion: number;
  drift: number;
  palette: string[]; // Hex цвета
  layers: Array<{
    scale: number;
    speed: number;
    opacity: number;
  }>;
}

/**
 * Uniforms для WebGL shader
 */
export interface Field1Uniforms {
  u_time: number;
  u_resolution: [number, number];
  u_mouse: [number, number];
  u_zoom: number;
  u_energy: number;
  u_turbulence: number;
  u_cohesion: number;
  u_drift: number;
  u_palette: Float32Array; // 4 цвета по 4 компонента (RGBA)
  u_layers: Float32Array; // 3 слоя по 3 параметра (scale, speed, opacity)
}

/**
 * Дефолтные параметры поля
 * 
 * Палитра: очень светлые, почти белые тона с легким теплом
 * Слои: очень медленное неспешное движение, очень воздушная прозрачность
 */
export const DEFAULT_FIELD1_PARAMS: Field1Params = {
  seed: 54321,
  energy: 1.0, // Максимальная энергия для максимальной яркости
  turbulence: 0.12, // Очень низкая турбулентность - очень спокойно
  cohesion: 0.20, // Очень низкая плотность - максимальная легкость
  drift: 0.025, // Очень медленное движение
  // Почти белые тона - максимально светлые
  palette: ['#FFFFFF', '#FFFFFF', '#FEFFFE', '#FFFFFF'], // Почти полностью белые
  layers: [
    // Дальний слой - очень медленный, большой масштаб, очень воздушный
    { scale: 8.0, speed: 0.25, opacity: 0.10 }, // Очень прозрачный
    // Средний слой - основное движение
    { scale: 4.0, speed: 0.30, opacity: 0.12 }, // Очень прозрачный
    // Ближний слой - чуть быстрее для динамики
    { scale: 2.5, speed: 0.35, opacity: 0.15 }, // Очень прозрачный
  ],
};

/**
 * Преобразует HEX цвет в RGBA (0.0-1.0)
 */
export function hexToRgba(hex: string): [number, number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return [1.0, 1.0, 1.0, 1.0]; // Дефолтный цвет - белый
  }
  
  return [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255,
    1.0,
  ];
}

/**
 * Преобразует параметры в uniforms для WebGL
 */
export function paramsToUniforms(
  params: Field1Params,
  time: number,
  resolution: [number, number],
  mouse: [number, number],
  zoom: number
): Field1Uniforms {
  // Преобразуем палитру в массив float
  const paletteArray = new Float32Array(16); // 4 цвета * 4 компонента
  params.palette.forEach((hex, i) => {
    const rgba = hexToRgba(hex);
    paletteArray[i * 4 + 0] = rgba[0];
    paletteArray[i * 4 + 1] = rgba[1];
    paletteArray[i * 4 + 2] = rgba[2];
    paletteArray[i * 4 + 3] = rgba[3];
  });

  // Преобразуем слои в массив float
  const layersArray = new Float32Array(9); // 3 слоя * 3 параметра
  params.layers.forEach((layer, i) => {
    layersArray[i * 3 + 0] = layer.scale;
    layersArray[i * 3 + 1] = layer.speed;
    layersArray[i * 3 + 2] = layer.opacity;
  });

  return {
    u_time: time,
    u_resolution: resolution,
    u_mouse: mouse,
    u_zoom: zoom,
    u_energy: params.energy,
    u_turbulence: params.turbulence,
    u_cohesion: params.cohesion,
    u_drift: params.drift,
    u_palette: paletteArray,
    u_layers: layersArray,
  };
}

